import { useState } from 'react';
import { Tab } from '@headlessui/react';
import {
  InformationCircleIcon,
  BookOpenIcon,
  ScaleIcon,
  GlobeAltIcon,
  HeartIcon,
  HomeIcon,
  ArrowPathIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { CreateSpeciesDto } from '../api/species';
import { IUCNStatus, CITESStatus } from '../utils/enums';
import api from '../api/client';
import { useGlobalConfig } from '../contexts/GlobalConfigContext';
import TabbedFormActions, { isNonEmptyString } from './ui/TabbedFormActions';
import {
  BrandModal,
  BrandModalBody,
  BrandModalHeader,
} from './ui/BrandModal';
import AmountInput from './ui/AmountInput';

interface AddSpeciesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (speciesData: CreateSpeciesDto) => void;
}

// Form data type with string fields for input handling
interface FormData {
  commonName: string;
  genus: string;
  species: string;
  subspecies?: string;
  family?: string;
  order?: string;
  typeCategory: string;
  originLocation: string;
  size: string;
  weight: string;
  colorDescription: string;
  captiveBreedingStatus: string;
  longevity: string;
  recommendedHousing: string;
  diet: string;
  nestBoxSize: string;
  clutchSize: string;
  fledgingAge: string;
  hatchingPeriod: string;
  daysBeforeBanding: string;
  bandSize: string;
  iucnStatus: IUCNStatus;
  citesStatus: CITESStatus;
  mutations?: string[];
  // Breeding fields
  breedingSeasonStart?: string;
  breedingSeasonEnd?: string;
  incubationPeriod?: string;
  nestingBehavior?: string;
  parentalCarePeriod?: string;
  sexualMaturityAge?: string;
  breedingPairCompatibility?: string;
  nestingMaterialPreferences?: string;
  breedingDifficultyLevel?: string;
  geneticDiversityConcerns?: string;
  // Health & Care fields
  lifespan?: string;
  dietRequirements?: string;
  housingRequirements?: string;
  commonHealthIssues?: string;
  vaccinationRequirements?: string;
  parasiteSusceptibility?: string;
  stressFactors?: string;
  environmentalRequirements?: string;
  socialBehavior?: string;
  handlingGuidelines?: string;
  quarantinePeriod?: string;
  veterinaryCareNeeds?: string;
  // Market & Sales fields
  marketValueRange?: string;
  marketDemandLevel?: string;
  legalStatus?: string;
  exportRestrictions?: string;
  marketTrends?: string;
  priceRangeMin?: string;
  priceRangeMax?: string;
  buyerPreferences?: string;
  competitionLevel?: string;
  seasonalDemandPatterns?: string;
  // AI Metadata fields
  aiDataSources?: string;
  aiConfidenceScore?: string;
  aiExtractionNotes?: string;
  manualReviewStatus?: string;
  aiProcessingVersion?: string;
}

interface FormErrors {
  form?: string;
  commonName?: string;
  genus?: string;
  species?: string;
  typeCategory?: string;
  originLocation?: string;
  colorDescription?: string;
  captiveBreedingStatus?: string;
  recommendedHousing?: string;
  diet?: string;
  nestBoxSize?: string;
  size?: string;
  weight?: string;
  longevity?: string;
  clutchSize?: string;
  fledgingAge?: string;
  hatchingPeriod?: string;
  daysBeforeBanding?: string;
  bandSize?: string;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

/** Extract a finite number from AI/form text like "15-18 cm" or "21 days". */
function parseFormNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  const match = String(value).trim().match(/-?\d+(\.\d+)?/);
  if (!match) return undefined;
  const num = Number(match[0]);
  return Number.isFinite(num) ? num : undefined;
}

function parseOptionalMonths(value: unknown): number | undefined {
  const raw = String(value ?? '').trim();
  if (!raw) return undefined;
  const leading = parseFormNumber(raw);
  if (leading === undefined) return undefined;
  const lower = raw.toLowerCase();
  if (lower.includes('year')) return Math.round(leading * 12);
  if (lower.includes('month')) return Math.round(leading);
  if (lower.includes('week')) return Math.max(1, Math.round((leading * 7) / 30));
  if (lower.includes('day')) return Math.max(1, Math.round(leading / 30));
  // Bare small numbers are usually years for sexual maturity.
  if (leading < 10) return Math.round(leading * 12);
  return Math.round(leading);
}

function clampConfidenceScore(value: unknown): number | undefined {
  const num = parseFormNumber(value);
  if (num === undefined) return undefined;
  if (num > 1 && num <= 100) return Math.min(1, num / 100);
  if (num < 0) return 0;
  if (num > 1) return 1;
  return num;
}

/** Coerce AI values (arrays/objects/numbers) into a form-safe string. */
function toFormText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => toFormText(item))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof value === 'object') {
    try {
      return Object.values(value as Record<string, unknown>)
        .map((item) => toFormText(item))
        .filter(Boolean)
        .join(', ');
    } catch {
      return '';
    }
  }
  return String(value).trim();
}

function cleanPayload<T extends Record<string, unknown>>(payload: T): Partial<T> {
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    if (typeof value === 'number' && !Number.isFinite(value)) continue;
    if (Array.isArray(value)) {
      // Keep string arrays (mutations / aiDataSources); stringify other arrays.
      if (value.every((item) => typeof item === 'string')) {
        const trimmed = value.map((item) => item.trim()).filter(Boolean);
        if (trimmed.length === 0) continue;
        cleaned[key] = trimmed;
        continue;
      }
      const text = toFormText(value);
      if (!text) continue;
      cleaned[key] = text;
      continue;
    }
    if (typeof value === 'object') {
      const text = toFormText(value);
      if (!text) continue;
      cleaned[key] = text;
      continue;
    }
    cleaned[key] = typeof value === 'string' ? value.trim() : value;
  }
  return cleaned as Partial<T>;
}

export default function AddSpeciesModal({ isOpen, onClose, onAdd }: AddSpeciesModalProps) {
  const { config } = useGlobalConfig();
  const [selectedTab, setSelectedTab] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    commonName: '',
    genus: '',
    species: '',
    subspecies: '',
    family: '',
    order: '',
    typeCategory: '',
    originLocation: '',
    size: '',
    weight: '',
    colorDescription: '',
    captiveBreedingStatus: '',
    longevity: '',
    recommendedHousing: '',
    diet: '',
    nestBoxSize: '',
    clutchSize: '',
    fledgingAge: '',
    hatchingPeriod: '',
    daysBeforeBanding: '',
    bandSize: '',
    iucnStatus: IUCNStatus.LEAST_CONCERN,
    citesStatus: CITESStatus.NOT_LISTED,
    mutations: [],
    // Breeding fields
    breedingSeasonStart: '',
    breedingSeasonEnd: '',
    incubationPeriod: '',
    nestingBehavior: '',
    parentalCarePeriod: '',
    sexualMaturityAge: '',
    breedingPairCompatibility: '',
    nestingMaterialPreferences: '',
    breedingDifficultyLevel: '',
    geneticDiversityConcerns: '',
    // Health & Care fields
    lifespan: '',
    dietRequirements: '',
    housingRequirements: '',
    commonHealthIssues: '',
    vaccinationRequirements: '',
    parasiteSusceptibility: '',
    stressFactors: '',
    environmentalRequirements: '',
    socialBehavior: '',
    handlingGuidelines: '',
    quarantinePeriod: '',
    veterinaryCareNeeds: '',
    // Market & Sales fields
    marketValueRange: '',
    marketDemandLevel: '',
    legalStatus: '',
    exportRestrictions: '',
    marketTrends: '',
    priceRangeMin: '',
    priceRangeMax: '',
    buyerPreferences: '',
    competitionLevel: '',
    seasonalDemandPatterns: '',
    // AI Metadata fields
    aiDataSources: '',
    aiConfidenceScore: '',
    aiExtractionNotes: '',
    manualReviewStatus: '',
    aiProcessingVersion: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiSuccess, setAiSuccess] = useState<{
    enhancedFields: number;
    preservedFields: number;
    confidenceScore: number;
    sources: string[];
    enhancedFieldNames: string[];
  } | null>(null);
  const [aiPreview, setAiPreview] = useState<{
    data: Partial<FormData>;
    confidenceScore: number;
    sources: string[];
  } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    // Clear error when field is modified
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[name as keyof FormErrors];
      return newErrors;
    });

    // Handle special cases
    if (name === 'mutations') {
      // Handle mutations array (value is already processed)
      setFormData(prev => ({ ...prev, [name]: value as unknown as string[] }));
    } else if (name === 'iucnStatus') {
      setFormData(prev => ({ ...prev, [name]: value as IUCNStatus }));
    } else if (name === 'citesStatus') {
      setFormData(prev => ({ ...prev, [name]: value as CITESStatus }));
    } else if (type === 'number') {
      // For number inputs, only update if empty or valid number
      if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Auto-generate fullTaxonomicName when genus, species, or subspecies changes
    if (name === 'genus' || name === 'species' || name === 'subspecies') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        fullTaxonomicName: `${name === 'genus' ? value : prev.genus} ${name === 'species' ? value : prev.species}${
          name === 'subspecies' && value ? ` ${value}` : prev.subspecies ? ` ${prev.subspecies}` : ''
        }`.trim()
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    const requiredStrings: Array<keyof FormData> = [
      'commonName',
      'genus',
      'species',
      'typeCategory',
      'originLocation',
      'colorDescription',
      'captiveBreedingStatus',
      'recommendedHousing',
      'diet',
      'nestBoxSize',
      'bandSize',
    ];
    requiredStrings.forEach((field) => {
      const value = formData[field];
      if (typeof value !== 'string' || !value.trim()) {
        newErrors[field] = 'This field is required';
      }
    });

    const numericFields = [
      'size',
      'weight',
      'longevity',
      'clutchSize',
      'fledgingAge',
      'hatchingPeriod',
      'daysBeforeBanding',
    ] as const;

    numericFields.forEach((field) => {
      const parsed = parseFormNumber(formData[field]);
      if (parsed === undefined || parsed < 0) {
        newErrors[field] = 'Please enter a valid positive number';
      }
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setAiError('Please fill all required fields before saving.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const size = parseFormNumber(formData.size)!;
    const weight = parseFormNumber(formData.weight)!;
    const longevity = parseFormNumber(formData.longevity)!;
    const clutchSize = parseFormNumber(formData.clutchSize)!;
    const fledgingAge = parseFormNumber(formData.fledgingAge)!;
    const hatchingPeriod = parseFormNumber(formData.hatchingPeriod)!;
    const daysBeforeBanding = parseFormNumber(formData.daysBeforeBanding)!;
    const incubationPeriod =
      parseFormNumber(formData.incubationPeriod) ?? hatchingPeriod;

    try {
      const speciesData = cleanPayload({
        commonName: formData.commonName,
        genus: formData.genus,
        species: formData.species,
        subspecies: formData.subspecies,
        family: formData.family,
        order: formData.order,
        typeCategory: formData.typeCategory,
        originLocation: formData.originLocation,
        colorDescription: formData.colorDescription,
        captiveBreedingStatus: formData.captiveBreedingStatus,
        recommendedHousing: formData.recommendedHousing || formData.housingRequirements,
        diet: formData.diet || formData.dietRequirements,
        nestBoxSize: formData.nestBoxSize,
        bandSize: formData.bandSize,
        iucnStatus: formData.iucnStatus,
        citesStatus: formData.citesStatus,
        mutations: formData.mutations?.length ? formData.mutations : undefined,
        size,
        weight,
        longevity,
        clutchSize,
        fledgingAge,
        hatchingPeriod,
        daysBeforeBanding,
        incubationPeriod,
        parentalCarePeriod: parseFormNumber(formData.parentalCarePeriod),
        sexualMaturityAge: parseOptionalMonths(formData.sexualMaturityAge),
        lifespan: parseFormNumber(formData.lifespan),
        quarantinePeriod: parseFormNumber(formData.quarantinePeriod),
        priceRangeMin: parseFormNumber(formData.priceRangeMin),
        priceRangeMax: parseFormNumber(formData.priceRangeMax),
        aiConfidenceScore: clampConfidenceScore(formData.aiConfidenceScore),
        breedingSeasonStart: formData.breedingSeasonStart,
        breedingSeasonEnd: formData.breedingSeasonEnd,
        nestingBehavior: formData.nestingBehavior,
        breedingPairCompatibility: formData.breedingPairCompatibility,
        nestingMaterialPreferences: formData.nestingMaterialPreferences,
        breedingDifficultyLevel: formData.breedingDifficultyLevel,
        geneticDiversityConcerns: formData.geneticDiversityConcerns,
        dietRequirements: toFormText(formData.dietRequirements),
        housingRequirements: toFormText(formData.housingRequirements),
        commonHealthIssues: toFormText(formData.commonHealthIssues),
        vaccinationRequirements: toFormText(formData.vaccinationRequirements),
        parasiteSusceptibility: toFormText(formData.parasiteSusceptibility),
        stressFactors: toFormText(formData.stressFactors),
        environmentalRequirements: toFormText(formData.environmentalRequirements),
        socialBehavior: toFormText(formData.socialBehavior),
        handlingGuidelines: toFormText(formData.handlingGuidelines),
        veterinaryCareNeeds: toFormText(formData.veterinaryCareNeeds),
        marketValueRange: formData.marketValueRange,
        marketDemandLevel: formData.marketDemandLevel,
        legalStatus: formData.legalStatus,
        exportRestrictions: formData.exportRestrictions,
        marketTrends: formData.marketTrends,
        buyerPreferences: formData.buyerPreferences,
        competitionLevel: formData.competitionLevel,
        seasonalDemandPatterns: formData.seasonalDemandPatterns,
        aiDataSources: formData.aiDataSources
          ? formData.aiDataSources.split(',').map((s) => s.trim()).filter(Boolean)
          : undefined,
        aiExtractionNotes: formData.aiExtractionNotes,
        manualReviewStatus: formData.manualReviewStatus,
        aiProcessingVersion: formData.aiProcessingVersion,
      }) as CreateSpeciesDto;

      await onAdd(speciesData);
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const mapAIResponseToFormData = (aiData: any): Partial<FormData> => {
    const mapped: Partial<FormData> = {};
    const empty = (value?: string) => !value || !String(value).trim();

    // Taxonomy
    if (aiData.order && empty(formData.order)) mapped.order = toFormText(aiData.order);
    if (aiData.family && empty(formData.family)) mapped.family = toFormText(aiData.family);
    if (aiData.genus && empty(formData.genus)) mapped.genus = toFormText(aiData.genus);
    if (aiData.species && empty(formData.species)) mapped.species = toFormText(aiData.species);
    if (aiData.subspecies && empty(formData.subspecies)) mapped.subspecies = toFormText(aiData.subspecies);
    if (aiData.commonName && empty(formData.commonName)) mapped.commonName = toFormText(aiData.commonName);

    // Basic Info fields - only map if current field is empty or enhance existing content
    if (aiData.size && empty(formData.size)) mapped.size = toFormText(aiData.size);
    if (aiData.weight && empty(formData.weight)) mapped.weight = toFormText(aiData.weight);
    if (aiData.colorDescription && empty(formData.colorDescription)) mapped.colorDescription = toFormText(aiData.colorDescription);
    if (aiData.captiveBreedingStatus && empty(formData.captiveBreedingStatus)) mapped.captiveBreedingStatus = toFormText(aiData.captiveBreedingStatus);
    if (aiData.longevity && empty(formData.longevity)) mapped.longevity = toFormText(aiData.longevity);
    if (aiData.recommendedHousing && empty(formData.recommendedHousing)) mapped.recommendedHousing = toFormText(aiData.recommendedHousing);
    if (aiData.diet && empty(formData.diet)) mapped.diet = toFormText(aiData.diet);
    if (aiData.nestBoxSize && empty(formData.nestBoxSize)) mapped.nestBoxSize = toFormText(aiData.nestBoxSize);
    if (aiData.clutchSize && empty(formData.clutchSize)) mapped.clutchSize = toFormText(aiData.clutchSize);
    if (aiData.fledgingAge && empty(formData.fledgingAge)) mapped.fledgingAge = toFormText(aiData.fledgingAge);
    if (aiData.hatchingPeriod && empty(formData.hatchingPeriod)) mapped.hatchingPeriod = toFormText(aiData.hatchingPeriod);
    if (aiData.incubationPeriod && empty(formData.incubationPeriod)) {
      mapped.incubationPeriod = toFormText(aiData.incubationPeriod);
    } else if (aiData.hatchingPeriod && empty(formData.incubationPeriod)) {
      mapped.incubationPeriod = toFormText(aiData.hatchingPeriod);
    }
    if (aiData.daysBeforeBanding && empty(formData.daysBeforeBanding)) mapped.daysBeforeBanding = toFormText(aiData.daysBeforeBanding);
    if (aiData.bandSize && empty(formData.bandSize)) mapped.bandSize = toFormText(aiData.bandSize);
    if (aiData.typeCategory && empty(formData.typeCategory)) mapped.typeCategory = toFormText(aiData.typeCategory);
    if (aiData.originLocation && empty(formData.originLocation)) mapped.originLocation = toFormText(aiData.originLocation);

    // Breeding fields
    if (aiData.breedingSeasonStart && empty(formData.breedingSeasonStart)) mapped.breedingSeasonStart = toFormText(aiData.breedingSeasonStart);
    if (aiData.breedingSeasonEnd && empty(formData.breedingSeasonEnd)) mapped.breedingSeasonEnd = toFormText(aiData.breedingSeasonEnd);
    if (aiData.nestingBehavior && empty(formData.nestingBehavior)) mapped.nestingBehavior = toFormText(aiData.nestingBehavior);
    if (aiData.parentalCarePeriod && empty(formData.parentalCarePeriod)) mapped.parentalCarePeriod = toFormText(aiData.parentalCarePeriod);
    if (aiData.sexualMaturityAge && empty(formData.sexualMaturityAge)) {
      mapped.sexualMaturityAge = toFormText(aiData.sexualMaturityAge);
    }
    if (aiData.breedingPairCompatibility && empty(formData.breedingPairCompatibility)) mapped.breedingPairCompatibility = toFormText(aiData.breedingPairCompatibility);
    if (aiData.nestingMaterialPreferences && empty(formData.nestingMaterialPreferences)) mapped.nestingMaterialPreferences = toFormText(aiData.nestingMaterialPreferences);
    if (aiData.breedingDifficultyLevel && empty(formData.breedingDifficultyLevel)) mapped.breedingDifficultyLevel = toFormText(aiData.breedingDifficultyLevel);
    if (aiData.geneticDiversityConcerns && empty(formData.geneticDiversityConcerns)) mapped.geneticDiversityConcerns = toFormText(aiData.geneticDiversityConcerns);

    // Health & Care fields
    if (aiData.lifespan && empty(formData.lifespan)) mapped.lifespan = toFormText(aiData.lifespan);
    if (aiData.dietRequirements && empty(formData.dietRequirements)) mapped.dietRequirements = toFormText(aiData.dietRequirements);
    if (aiData.housingRequirements && empty(formData.housingRequirements)) mapped.housingRequirements = toFormText(aiData.housingRequirements);
    if (aiData.commonHealthIssues && empty(formData.commonHealthIssues)) mapped.commonHealthIssues = toFormText(aiData.commonHealthIssues);
    if (aiData.vaccinationRequirements && empty(formData.vaccinationRequirements)) mapped.vaccinationRequirements = toFormText(aiData.vaccinationRequirements);
    if (aiData.parasiteSusceptibility && empty(formData.parasiteSusceptibility)) mapped.parasiteSusceptibility = toFormText(aiData.parasiteSusceptibility);
    if (aiData.stressFactors && empty(formData.stressFactors)) mapped.stressFactors = toFormText(aiData.stressFactors);
    if (aiData.environmentalRequirements && empty(formData.environmentalRequirements)) mapped.environmentalRequirements = toFormText(aiData.environmentalRequirements);
    if (aiData.socialBehavior && empty(formData.socialBehavior)) mapped.socialBehavior = toFormText(aiData.socialBehavior);
    if (aiData.handlingGuidelines && empty(formData.handlingGuidelines)) mapped.handlingGuidelines = toFormText(aiData.handlingGuidelines);
    if (aiData.quarantinePeriod && empty(formData.quarantinePeriod)) mapped.quarantinePeriod = toFormText(aiData.quarantinePeriod);
    if (aiData.veterinaryCareNeeds && empty(formData.veterinaryCareNeeds)) mapped.veterinaryCareNeeds = toFormText(aiData.veterinaryCareNeeds);

    // Market & Sales fields
    if (aiData.marketValueRange && empty(formData.marketValueRange)) mapped.marketValueRange = toFormText(aiData.marketValueRange);
    if (aiData.marketDemandLevel && empty(formData.marketDemandLevel)) mapped.marketDemandLevel = toFormText(aiData.marketDemandLevel);
    if (aiData.legalStatus && empty(formData.legalStatus)) mapped.legalStatus = toFormText(aiData.legalStatus);
    if (aiData.priceRangeMin) {
      const price = Number(aiData.priceRangeMin);
      if (empty(formData.priceRangeMin) && Number.isFinite(price)) {
        mapped.priceRangeMin = String(price);
      }
    }
    if (aiData.priceRangeMax) {
      const price = Number(aiData.priceRangeMax);
      if (empty(formData.priceRangeMax) && Number.isFinite(price)) {
        mapped.priceRangeMax = String(price);
      }
    }
    if (aiData.marketTrends && empty(formData.marketTrends)) mapped.marketTrends = toFormText(aiData.marketTrends);
    if (aiData.exportRestrictions && empty(formData.exportRestrictions)) mapped.exportRestrictions = toFormText(aiData.exportRestrictions);
    if (aiData.buyerPreferences && empty(formData.buyerPreferences)) mapped.buyerPreferences = toFormText(aiData.buyerPreferences);
    if (aiData.competitionLevel && empty(formData.competitionLevel)) mapped.competitionLevel = toFormText(aiData.competitionLevel);
    if (aiData.seasonalDemandPatterns && empty(formData.seasonalDemandPatterns)) mapped.seasonalDemandPatterns = toFormText(aiData.seasonalDemandPatterns);

    // AI Metadata fields - always update these
    if (aiData.aiDataSources) mapped.aiDataSources = Array.isArray(aiData.aiDataSources)
      ? aiData.aiDataSources.map((s: unknown) => toFormText(s)).filter(Boolean).join(', ')
      : toFormText(aiData.aiDataSources);
    if (aiData.aiConfidenceScore != null) mapped.aiConfidenceScore = toFormText(aiData.aiConfidenceScore);
    if (aiData.aiExtractionNotes) mapped.aiExtractionNotes = toFormText(aiData.aiExtractionNotes);
    if (aiData.aiProcessingVersion) mapped.aiProcessingVersion = toFormText(aiData.aiProcessingVersion);

    return mapped;
  };

  const handleAIAutoComplete = async () => {
    setAiLoading(true);
    setAiError(null);
    setAiSuccess(null); // Clear previous success state
    setAiPreview(null); // Clear previous preview
    try {
      // Build comprehensive search data
      const searchData: any = {};
      
      // Use common name if available
      if (formData.commonName?.trim()) {
        searchData.commonName = formData.commonName.trim();
      }
      
      // Use genus and species if available
      if (formData.genus?.trim() && formData.species?.trim()) {
        searchData.genus = formData.genus.trim();
        searchData.species = formData.species.trim();
        searchData.scientificName = `${formData.genus.trim()} ${formData.species.trim()}`;
      }
      
      // Use subspecies if available
      if (formData.subspecies?.trim()) {
        searchData.subspecies = formData.subspecies.trim();
        if (searchData.scientificName) {
          searchData.scientificName += ` ${formData.subspecies.trim()}`;
        }
      }
      
      // Validate that we have at least some search criteria
      if (!searchData.commonName && !searchData.scientificName) {
        setAiError('Please enter a common name or genus/species first.');
        setAiLoading(false);
        return;
      }
      
      // Use postWithTimeout with 90 second timeout for enhanced AI scraping
      const response = await api.postWithTimeout('/species/ai-scrape-data', searchData, 45000);
      const responseData = response.data as any;
      if (responseData && responseData.success && responseData.data) {
        // Map AI response data to form fields with content preservation
        const mappedData = mapAIResponseToFormData(responseData.data);
        
        // Count how many fields were enhanced vs preserved
        const enhancedFields = Object.keys(mappedData).length;
        const totalFields = Object.keys(formData).length;
        const preservedFields = totalFields - enhancedFields;
        
        // Merge AI data into form
        setFormData(prev => ({
          ...prev,
          ...mappedData
        }));
        
        // Show success message with details
        const confidenceScore = responseData.data.aiConfidenceScore || 0;
        const sources = responseData.sources || [];
        
        // Create a success notification
        const successMessage = `AI Auto-Complete completed successfully!
        
Enhanced ${enhancedFields} fields with suggested values
Preserved ${preservedFields} existing fields
Confidence Score: ${(confidenceScore * 100).toFixed(1)}% (model estimates — review before saving)`;
        
        setAiSuccess({ enhancedFields, preservedFields, confidenceScore, sources, enhancedFieldNames: Object.keys(mappedData) });
        setAiPreview({ data: mappedData, confidenceScore, sources });
        
      } else {
        setAiError(responseData?.error || 'AI auto-complete failed.');
      }
    } catch (err: any) {
      setAiError(err?.response?.data?.message || err.message || 'AI auto-complete failed.');
    } finally {
      setAiLoading(false);
    }
  };

  const categories = [
    {
      name: 'Basic Info',
      icon: InformationCircleIcon,
      content: <BasicInfoTab formData={formData} handleChange={handleChange} errors={errors} />
    },
    {
      name: 'Taxonomy',
      icon: BookOpenIcon,
      content: <TaxonomyTab formData={formData} handleChange={handleChange} />
    },
    {
      name: 'Physical Traits',
      icon: ScaleIcon,
      content: <PhysicalTraitsTab formData={formData} handleChange={handleChange} errors={errors} />
    },
    {
      name: 'Conservation',
      icon: GlobeAltIcon,
      content: <ConservationTab formData={formData} handleChange={handleChange} />
    },
    {
      name: 'Care Guide',
      icon: HeartIcon,
      content: <CareGuideTab formData={formData} handleChange={handleChange} />
    },
    {
      name: 'Breeding',
      icon: HomeIcon,
      content: <BreedingTab formData={formData} handleChange={handleChange} />
    },
    {
      name: 'Health & Care',
      icon: HeartIcon,
      content: <HealthCareTab formData={formData} handleChange={handleChange} />
    },
    {
      name: 'Market & Sales',
      icon: ScaleIcon,
              content: <MarketTab formData={formData} handleChange={handleChange} config={config} />
    },
    {
      name: 'AI Metadata',
      icon: InformationCircleIcon,
      content: <AIMetadataTab formData={formData} handleChange={handleChange} />
    },
  ];

  const requiredStringFields: Array<keyof FormData> = [
    'commonName',
    'genus',
    'species',
    'typeCategory',
    'originLocation',
    'colorDescription',
    'captiveBreedingStatus',
    'recommendedHousing',
    'diet',
    'nestBoxSize',
    'bandSize',
  ];
  const requiredNumericFields = [
    'size',
    'weight',
    'longevity',
    'clutchSize',
    'fledgingAge',
    'hatchingPeriod',
    'daysBeforeBanding',
  ] as const;
  const canSubmit =
    requiredStringFields.every((field) => isNonEmptyString(formData[field])) &&
    requiredNumericFields.every((field) => {
      const parsed = parseFormNumber(formData[field]);
      return parsed !== undefined && parsed >= 0;
    });

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-6xl">
      <BrandModalHeader title="Add New Species" onClose={onClose} />
      <BrandModalBody>
        {errors.form && (
          <div className="mb-4 text-sm text-red-600">
            {errors.form}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full">
          <div className="w-full">
            <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
              <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-6">
                {categories.map((category) => (
                  <Tab
                    key={category.name}
                    className={({ selected }) =>
                      classNames(
                        'w-full rounded-lg py-2.5 px-3 text-sm font-medium leading-5 flex items-center justify-center space-x-2',
                        'ring-white ring-opacity-60 ring-offset-2 ring-offset-indigo-400 focus:outline-none focus:ring-2',
                        selected
                          ? 'bg-white shadow text-cyan-700 border border-indigo-200'
                          : 'text-slate-600 hover:bg-white/[0.8] hover:text-gray-800'
                      )
                    }
                  >
                    <category.icon className="h-4 w-4" />
                    <span className="hidden sm:block">{category.name}</span>
                  </Tab>
                ))}
              </Tab.List>
              
              <Tab.Panels className="mt-2">
                {categories.map((category, idx) => (
                  <Tab.Panel
                    key={idx}
                    className={classNames(
                      'rounded-xl bg-white p-6 shadow-sm border min-h-[400px]',
                      'ring-white ring-opacity-60 ring-offset-2 ring-offset-indigo-400 focus:outline-none focus:ring-2'
                    )}
                  >
                    {category.content}
                  </Tab.Panel>
                ))}
              </Tab.Panels>
            </Tab.Group>
          </div>

          <TabbedFormActions
            onCancel={onClose}
            tabCount={categories.length}
            selectedTab={selectedTab}
            onTabChange={setSelectedTab}
            canSubmit={canSubmit}
            submitLabel="Add Species"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleAIAutoComplete}
              className="inline-flex items-center px-4 py-2 border border-cyan-500 text-cyan-600 bg-white hover:bg-cyan-50 rounded-md text-sm font-medium shadow-sm disabled:opacity-50 transition-colors duration-200"
              disabled={aiLoading}
              title="AI-assisted form fill from model knowledge — review all values before saving"
            >
              {aiLoading ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 animate-spin mr-2" />
                  <span>AI Processing...</span>
                </>
              ) : (
                <>
                  <SparklesIcon className="h-5 w-5 mr-2" />
                  <span>AI Auto-Complete</span>
                </>
              )}
            </button>
            {aiLoading && (
              <div className="text-xs text-slate-500">
                Generating suggestions…
              </div>
            )}
          </div>
          {aiError && (
            <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded border border-red-200">
              <strong>AI Error:</strong> {aiError}
            </div>
          )}
          {aiSuccess && (
            <div className="mt-2 text-sm text-green-600 bg-green-50 p-2 rounded border border-green-200">
              <strong>AI Success:</strong> {aiSuccess.enhancedFields} fields enhanced, {aiSuccess.preservedFields} fields preserved. Confidence Score: {aiSuccess.confidenceScore.toFixed(1)}%. Sources: {aiSuccess.sources.slice(0, 3).join(', ')}{aiSuccess.sources.length > 3 ? '...' : ''}
              {aiSuccess.enhancedFieldNames.length > 0 && (
                <div className="mt-1 text-xs text-green-700">
                  <strong>Enhanced fields:</strong> {aiSuccess.enhancedFieldNames.slice(0, 5).join(', ')}{aiSuccess.enhancedFieldNames.length > 5 ? '...' : ''}
                </div>
              )}
            </div>
          )}
          {aiPreview && (
            <div className="mt-2 text-sm text-cyan-600 bg-cyan-50 p-2 rounded border border-blue-200">
              <strong>AI Preview:</strong>
              <p>Confidence Score: {(aiPreview.confidenceScore * 100).toFixed(1)}%</p>
              <p>Sources: {aiPreview.sources.slice(0, 3).join(', ')}{aiPreview.sources.length > 3 ? '...' : ''}</p>
              <p>Enhanced Fields: {Object.keys(aiPreview.data).slice(0, 8).join(', ')}{Object.keys(aiPreview.data).length > 8 ? '...' : ''}</p>
            </div>
          )}
        </form>
      </BrandModalBody>
    </BrandModal>
  );
}

// Tab Components
interface TabProps {
  formData: FormData;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  errors?: FormErrors;
}

interface MarketTabProps extends TabProps {
  config: any;
}

function BasicInfoTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Basic Information</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="commonName" className="block text-sm font-medium text-slate-700">
              Common Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="commonName"
              id="commonName"
              value={formData.commonName}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., Canary, Cockatiel, Zebra Finch"
            />
          </div>

          <div>
            <label htmlFor="typeCategory" className="block text-sm font-medium text-slate-700">
              Type Category <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="typeCategory"
              id="typeCategory"
              value={formData.typeCategory}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., Finch, Parrot, Canary"
            />
          </div>

          <div>
            <label htmlFor="originLocation" className="block text-sm font-medium text-slate-700">
              Origin Location <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="originLocation"
              id="originLocation"
              value={formData.originLocation}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., Australia, South America, Africa"
            />
          </div>

          <div>
            <label htmlFor="captiveBreedingStatus" className="block text-sm font-medium text-slate-700">
              Captive Breeding Status <span className="text-red-500">*</span>
            </label>
            <select
              name="captiveBreedingStatus"
              id="captiveBreedingStatus"
              value={formData.captiveBreedingStatus}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            >
              <option key="empty-option-1753461297278" value="">Select status</option>
              <option key="common-1753461297278" value="common">Common</option>
              <option key="uncommon-1753461297278" value="uncommon">Uncommon</option>
              <option key="rare-1753461297278" value="rare">Rare</option>
              <option key="very_rare-1753461297278" value="very_rare">Very Rare</option>
            </select>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700">
          Known Mutations
        </label>
        <p className="mt-1 text-sm text-slate-600">
          Mutation catalogs are loaded automatically after save for supported species (e.g. budgerigar, Indian ringneck).
          Use the Genetics tab when editing the species to view or seed the catalog.
        </p>
      </div>
    </div>
  );
}

function TaxonomyTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Scientific Classification</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="genus" className="block text-sm font-medium text-slate-700">
              Genus <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="genus"
              id="genus"
              value={formData.genus}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., Serinus, Nymphicus, Taeniopygia"
            />
          </div>

          <div>
            <label htmlFor="species" className="block text-sm font-medium text-slate-700">
              Species <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="species"
              id="species"
              value={formData.species}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., canaria, hollandicus, guttata"
            />
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="subspecies" className="block text-sm font-medium text-slate-700">
              Subspecies
            </label>
            <input
              type="text"
              name="subspecies"
              id="subspecies"
              value={formData.subspecies}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="Leave blank if no subspecies"
            />
            <p className="mt-2 text-sm text-slate-500">
              Enter subspecies name if applicable, otherwise leave blank.
            </p>
          </div>

          <div>
            <label htmlFor="family" className="block text-sm font-medium text-slate-700">
              Family
            </label>
            <input
              type="text"
              name="family"
              id="family"
              value={formData.family}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="e.g., Fringillidae, Psittacidae, Estrildidae"
            />
            <p className="mt-2 text-sm text-slate-500">
              The taxonomic family to which this species belongs.
            </p>
          </div>

          <div>
            <label htmlFor="order" className="block text-sm font-medium text-slate-700">
              Order
            </label>
            <input
              type="text"
              name="order"
              id="order"
              value={formData.order}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="e.g., Passeriformes, Psittaciformes"
            />
            <p className="mt-2 text-sm text-slate-500">
              The taxonomic order to which this species belongs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function PhysicalTraitsTab({ formData, handleChange, errors }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Physical Characteristics</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="size" className="block text-sm font-medium text-slate-700">
              Size (cm) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.1"
              name="size"
              id="size"
              value={formData.size}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                errors?.size 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-cyan-500 focus:ring-cyan-500'
              }`}
              required
              placeholder="e.g., 12.5"
            />
            {errors?.size && (
              <p className="mt-1 text-sm text-red-600">{errors.size}</p>
            )}
          </div>

          <div>
            <label htmlFor="weight" className="block text-sm font-medium text-slate-700">
              Weight (g) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.1"
              name="weight"
              id="weight"
              value={formData.weight}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                errors?.weight
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-cyan-500 focus:ring-cyan-500'
              }`}
              required
              placeholder="e.g., 20.5"
            />
            {errors?.weight && (
              <p className="mt-1 text-sm text-red-600">{errors.weight}</p>
            )}
          </div>

          <div>
            <label htmlFor="longevity" className="block text-sm font-medium text-slate-700">
              Longevity (years) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="longevity"
              id="longevity"
              value={formData.longevity}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                errors?.longevity
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-cyan-500 focus:ring-cyan-500'
              }`}
              required
              placeholder="e.g., 8"
            />
            {errors?.longevity && (
              <p className="mt-1 text-sm text-red-600">{errors.longevity}</p>
            )}
          </div>

          <div>
            <label htmlFor="bandSize" className="block text-sm font-medium text-slate-700">
              Band Size <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="bandSize"
              id="bandSize"
              value={formData.bandSize}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                errors?.bandSize
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-cyan-500 focus:ring-cyan-500'
              }`}
              required
              placeholder="e.g., 2.9mm, 3.2mm, 4.0mm"
            />
            {errors?.bandSize && (
              <p className="mt-1 text-sm text-red-600">{errors.bandSize}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="colorDescription" className="block text-sm font-medium text-slate-700">
            Color Description <span className="text-red-500">*</span>
          </label>
          <textarea
            name="colorDescription"
            id="colorDescription"
            value={formData.colorDescription}
            onChange={handleChange}
            rows={3}
            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            required
            placeholder="Describe the natural coloration and markings..."
          />
        </div>
      </div>
    </div>
  );
}

function ConservationTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Conservation Status</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="iucnStatus" className="block text-sm font-medium text-slate-700">
              IUCN Status <span className="text-red-500">*</span>
            </label>
            <select
              name="iucnStatus"
              id="iucnStatus"
              value={formData.iucnStatus}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            >
              <option key="empty-option-1753461297278" value="">Select status</option>
              <option key="EX-1753461297278" value="EX">Extinct (EX)</option>
              <option key="EW-1753461297278" value="EW">Extinct in the Wild (EW)</option>
              <option key="CR-1753461297278" value="CR">Critically Endangered (CR)</option>
              <option key="EN-1753461297278" value="EN">Endangered (EN)</option>
              <option key="VU-1753461297278" value="VU">Vulnerable (VU)</option>
              <option key="NT-1753461297278" value="NT">Near Threatened (NT)</option>
              <option key="LC-1753461297278" value="LC">Least Concern (LC)</option>
              <option key="DD-1753461297278" value="DD">Data Deficient (DD)</option>
              <option key="NE-1753461297278" value="NE">Not Evaluated (NE)</option>
            </select>
          </div>

          <div>
            <label htmlFor="citesStatus" className="block text-sm font-medium text-slate-700">
              CITES Status <span className="text-red-500">*</span>
            </label>
            <select
              name="citesStatus"
              id="citesStatus"
              value={formData.citesStatus}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            >
              <option key="empty-option-1753461297278" value="">Select status</option>
              <option key="I-1753461297278" value="I">Appendix I</option>
              <option key="II-1753461297278" value="II">Appendix II</option>
              <option key="III-1753461297278" value="III">Appendix III</option>
              <option key="NL-1753461297278" value="NL">Not Listed</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

function CareGuideTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Care Requirements</h3>
        <div className="space-y-6">
          <div>
            <label htmlFor="recommendedHousing" className="block text-sm font-medium text-slate-700">
              Recommended Housing <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="recommendedHousing"
              id="recommendedHousing"
              value={formData.recommendedHousing}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., Flight cage minimum 120cm x 60cm x 80cm"
            />
            <p className="mt-2 text-sm text-slate-500">
              Specify the minimum housing requirements for this species.
            </p>
          </div>

          <div>
            <label htmlFor="diet" className="block text-sm font-medium text-slate-700">
              Diet <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="diet"
              id="diet"
              value={formData.diet}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., High-quality seed mix, fresh vegetables, occasional fruits"
            />
            <p className="mt-2 text-sm text-slate-500">
              Describe the optimal diet for this species.
            </p>
          </div>

          <div>
            <label htmlFor="nestBoxSize" className="block text-sm font-medium text-slate-700">
              Nest Box Size <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="nestBoxSize"
              id="nestBoxSize"
              value={formData.nestBoxSize}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., 12cm x 12cm x 15cm"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Breeding Information</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="clutchSize" className="block text-sm font-medium text-slate-700">
              Clutch Size <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="clutchSize"
              id="clutchSize"
              value={formData.clutchSize}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., 4"
            />
          </div>

          <div>
            <label htmlFor="fledgingAge" className="block text-sm font-medium text-slate-700">
              Fledging Age (days) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="fledgingAge"
              id="fledgingAge"
              value={formData.fledgingAge}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., 21"
            />
          </div>

          <div>
            <label htmlFor="hatchingPeriod" className="block text-sm font-medium text-slate-700">
              Hatching Period (days) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="hatchingPeriod"
              id="hatchingPeriod"
              value={formData.hatchingPeriod}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., 14"
            />
          </div>

          <div>
            <label htmlFor="daysBeforeBanding" className="block text-sm font-medium text-slate-700">
              Days Before Banding <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="daysBeforeBanding"
              id="daysBeforeBanding"
              value={formData.daysBeforeBanding}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., 7"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function BreedingTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Breeding Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">Breeding Season Start</label>
            <input type="text" name="breedingSeasonStart" value={formData.breedingSeasonStart} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Spring" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Breeding Season End</label>
            <input type="text" name="breedingSeasonEnd" value={formData.breedingSeasonEnd} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Summer" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Incubation Period (days)</label>
            <input type="number" name="incubationPeriod" value={formData.incubationPeriod} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., 14" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Nesting Behavior</label>
            <input type="text" name="nestingBehavior" value={formData.nestingBehavior} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Cavity nester" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Parental Care Period (days)</label>
            <input type="number" name="parentalCarePeriod" value={formData.parentalCarePeriod} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., 21" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Sexual Maturity Age (months)</label>
            <input type="number" name="sexualMaturityAge" value={formData.sexualMaturityAge} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., 24 for 2 years" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Breeding Pair Compatibility</label>
            <input type="text" name="breedingPairCompatibility" value={formData.breedingPairCompatibility} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Monogamous pairs" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Nesting Material Preferences</label>
            <input type="text" name="nestingMaterialPreferences" value={formData.nestingMaterialPreferences} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Soft materials" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Breeding Difficulty Level</label>
            <input type="text" name="breedingDifficultyLevel" value={formData.breedingDifficultyLevel} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Easy" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Genetic Diversity Concerns</label>
            <input type="text" name="geneticDiversityConcerns" value={formData.geneticDiversityConcerns} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Low genetic diversity" />
          </div>
        </div>
      </div>
    </div>
  );
}

function HealthCareTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Health & Care Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">Lifespan (years)</label>
            <input type="number" name="lifespan" value={formData.lifespan} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., 8" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Diet Requirements</label>
            <input type="text" name="dietRequirements" value={formData.dietRequirements} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., High protein diet" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Housing Requirements</label>
            <input type="text" name="housingRequirements" value={formData.housingRequirements} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Large flight cage" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Veterinary Care Needs</label>
            <input type="text" name="veterinaryCareNeeds" value={formData.veterinaryCareNeeds} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Annual checkups" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Common Health Issues</label>
            <input type="text" name="commonHealthIssues" value={formData.commonHealthIssues} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Respiratory infections" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Vaccination Requirements</label>
            <input type="text" name="vaccinationRequirements" value={formData.vaccinationRequirements} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., None required" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Parasite Susceptibility</label>
            <input type="text" name="parasiteSusceptibility" value={formData.parasiteSusceptibility} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Low" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Stress Factors</label>
            <input type="text" name="stressFactors" value={formData.stressFactors} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Loud noises" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Environmental Requirements</label>
            <input type="text" name="environmentalRequirements" value={formData.environmentalRequirements} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., 20-25\u00b0C, 50-70% humidity" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Social Behavior</label>
            <input type="text" name="socialBehavior" value={formData.socialBehavior} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Social, pairs or groups" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Handling Guidelines</label>
            <input type="text" name="handlingGuidelines" value={formData.handlingGuidelines} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Gentle handling only" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Quarantine Period (days)</label>
            <input type="number" name="quarantinePeriod" value={formData.quarantinePeriod} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., 30" />
          </div>
        </div>
      </div>
    </div>
  );
}

function MarketTab({ formData, handleChange, config }: MarketTabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Market & Sales Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">Market Value Range</label>
            <input type="text" name="marketValueRange" value={formData.marketValueRange} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder={`e.g., ${config?.currency?.symbol || 'R'}50-200`} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Market Demand Level</label>
            <input type="text" name="marketDemandLevel" value={formData.marketDemandLevel} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., High" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Legal Status</label>
            <input type="text" name="legalStatus" value={formData.legalStatus} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Legal to own" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Export Restrictions</label>
            <input type="text" name="exportRestrictions" value={formData.exportRestrictions} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., None" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Price Range Min ({config?.currency?.default || 'ZAR'})</label>
            <AmountInput
              id="priceRangeMin"
              name="priceRangeMin"
              value={formData.priceRangeMin}
              onChange={(v) =>
                handleChange({
                  target: { name: 'priceRangeMin', value: v != null ? String(v) : '' },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              allowEmpty
              placeholder="e.g., 50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Price Range Max ({config?.currency?.default || 'ZAR'})</label>
            <AmountInput
              id="priceRangeMax"
              name="priceRangeMax"
              value={formData.priceRangeMax}
              onChange={(v) =>
                handleChange({
                  target: { name: 'priceRangeMax', value: v != null ? String(v) : '' },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              allowEmpty
              placeholder="e.g., 200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Market Trends</label>
            <input type="text" name="marketTrends" value={formData.marketTrends} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Increasing demand" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Buyer Preferences</label>
            <input type="text" name="buyerPreferences" value={formData.buyerPreferences} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Young birds preferred" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Competition Level</label>
            <input type="text" name="competitionLevel" value={formData.competitionLevel} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Low" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Seasonal Demand Patterns</label>
            <input type="text" name="seasonalDemandPatterns" value={formData.seasonalDemandPatterns} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="e.g., Higher in spring" />
          </div>
        </div>
      </div>
    </div>
  );
}

function AIMetadataTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700">AI Data Sources</label>
          <input type="text" name="aiDataSources" value={formData.aiDataSources} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">AI Confidence Score</label>
          <input type="number" name="aiConfidenceScore" value={formData.aiConfidenceScore} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">AI Extraction Notes</label>
          <textarea name="aiExtractionNotes" value={formData.aiExtractionNotes} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">Manual Review Status</label>
          <input type="text" name="manualReviewStatus" value={formData.manualReviewStatus} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">AI Processing Version</label>
          <input type="text" name="aiProcessingVersion" value={formData.aiProcessingVersion} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
        </div>
      </div>
    </div>
  );
} 
